import {getTestProps} from '@benchling/js-test-utils';
import React from 'react';
import isEqual from 'react-fast-compare';
import {connect} from 'react-redux';
import _, {compact} from 'underscore';

import {deprecatedTrackEvent} from 'analytics/Analytics';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import {LEGACY_WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {CollapsedTableReduxActions} from 'notebook/tables/collapse/reduceCollapsedTablesUI';
import {Table} from 'notebook/tables/constants/TableConstants';
import {SimpleDownloadAsCSVButton} from 'notebook/tables/export/DownloadAsCSVButton';
import getPlainTextRawTable from 'notebook/tables/export/getPlainTextRawTable';
import {
  createInputTableCollapseId,
  shouldCollapseTableInitially,
} from 'notebook/tables/store/logic/TableVisibilityLogic';
import {BenchlingState} from 'redux-stores/reduceApp';
import CreateRequestFromSamplesButton, {
  SampleLookupInfoForCreateRequest,
} from 'requests/creation/CreateRequestFromSamplesButton';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import ModalAnchor from 'ui-library/ModalAnchor';
import * as RichObjectTable from 'ui-library/RichObjectTable';
import {ASCENDING_SORT, DESCENDING_SORT, SortDirection} from 'ui-library/scrollable/ScrollableTableConstants';
import ScrollableTableHeaderCell from 'ui-library/scrollable/ScrollableTableHeaderCell';
import {TableCollapserContext} from 'ui-library/tables/TableCollapserContext';
import * as ExportUtil from 'util/ExportUtil';
import memoizeLastCall from 'util/memoizeLastCall';
import {initiateTextDownload} from 'util/TextToBlobUtil';
import {ElementMouseEvent} from 'util/Types';
import BulkAddSamplesToInputTableFlow from 'workflows/input-tables/BulkAddSamplesToInputTableFlow';
import {WorkflowSelector, WorkflowsState} from 'workflows/reduceWorkflows';
import InputTableRetriever from 'workflows/stage-entries/components/InputTableRetriever';
import TableContainerActionButtons from 'workflows/stage-entries/components/TableContainerActionButtons';
import {
  CONTAINER_COLUMN,
  getInputRowDatas,
  getSortStateFromInputTableConfig,
  InputTableRow,
  SAMPLE_COLUMN,
  sortInputRowDatas,
  STAGE_RUN_COLUMN,
} from 'workflows/stage-entries/InputTableUtil';
import * as StageEntryActions from 'workflows/stage-entries/StageEntryActions';
import StageEntryStore from 'workflows/stage-entries/StageEntryStore';
import * as StageRunActions from 'workflows/StageRunActions';
import {getUniqueExperimentalConditionSummaries} from 'workflows/util/ExperimentalConditionUtil';
import {
  CONTAINER_COLUMN_LABEL,
  INPUT_TABLE_SORT_BY_TYPE,
  InputTableConfig,
  SAMPLE_COLUMN_LABEL,
  STAGE_RUN_COLUMN_LABEL,
  StageRun,
  StageRunSample,
} from 'workflows/WorkflowConstants';

import AddInputSampleModal from './AddInputSampleModal';
import EmptyInputTableRow from './EmptyInputTableRow';
import {InputType} from './ExternalInputPicker';
import ExternalSampleInputTableRow from './ExternalSampleInputTableRow';
import ParentSampleInputTableRow from './ParentSampleInputTableRow';
import PendingInputTableRow from './PendingInputTableRow';
import {UnversionedTable} from './UnversionedTable';
import {getSelectedSampleIds, getSelectedSamples} from './WorkflowTableUtil';

const SORT_ORDERS = [ASCENDING_SORT, DESCENDING_SORT, null];
function getDimensions({
  orderedInputSamples,
  parentIdsByStageRunId,
  stageRunsById,
  stageRuns,
}: {
  orderedInputSamples: Array<{stageRunId: string; stageRunSampleId: string}>;
  parentIdsByStageRunId: {[stageRunId: string]: Array<string>};
  stageRunsById: {[stageRunId: string]: StageRun};
  stageRuns: Array<StageRun>;
}): {rowCount: number; cellCount: number} {
  const rowDatas = getInputRowDatas(orderedInputSamples, parentIdsByStageRunId, stageRunsById, stageRuns);
  const rowCount = rowDatas.length;
  const experimentalConditionCount = getUniqueExperimentalConditionSummaries(stageRuns).length;
  const columnCount = 4 + experimentalConditionCount; // checkbox, sample, container, stage run, experimental conditions
  return {
    rowCount,
    cellCount: rowCount * columnCount,
  };
}

// NOTE: Redux accepts `makeMapStateToProps` in `connect`, which is run once per instance initialization,
// whose returned mapStateToProps is called once per render. This allows us to use per-instance memoization
// within mapStateToProps.
function makeMapStateToProps(): (state: BenchlingState, props: BaseInputTableProps) => ConnectedProps {
  // Each component instance gets its own memoized mapSubStateToProps.
  const mapSubStateToProps = memoizeLastCall(
    (
      workflows: WorkflowsState,
      reduxCollapsedState: boolean | undefined,
      stageEntryId: string,
      viewer: boolean | undefined
    ) => {
      const workflowSelector = new WorkflowSelector(workflows);
      const orderedInputSamples = workflowSelector.getOrderedInputSampleKeys(stageEntryId);
      const stageEntry = workflowSelector.getStageEntry(stageEntryId);
      const inputTableConfig = stageEntry == null ? null : stageEntry.inputTableConfig;
      const stageRuns = workflowSelector.getOrderedStageRunsForEntry(stageEntryId);
      const stageRunSampleById = workflowSelector.getStageRunSampleById();
      const parentIdsByStageRunId = workflowSelector.getParentIdsByStageRunId(
        stageRuns.map((stageRun: StageRun) => stageRun.id)
      );
      if (!parentIdsByStageRunId) {
        return {
          parentIdsByStageRunId: null,
          stageRunsById: null,
          stageRunSampleById,
          stageRuns,
          inputTableConfig,
          orderedInputSamples,
          isCollapsed: false,
          isInitiallyCollapsed: false,
        };
      }
      const stageRunIds = [
        ...Object.keys(parentIdsByStageRunId),
        // eslint-disable-next-line no-restricted-syntax
        ..._.flatten(Object.values(parentIdsByStageRunId)),
      ];
      const stageRunsById = workflowSelector.getStageRunsById(stageRunIds);
      const {isCollapsed, isInitiallyCollapsed} = getIsCollapsed(
        reduxCollapsedState,
        viewer,
        orderedInputSamples,
        parentIdsByStageRunId,
        stageRunsById,
        stageRuns
      );
      return {
        parentIdsByStageRunId,
        stageRunsById,
        stageRunSampleById,
        stageRuns,
        inputTableConfig,
        orderedInputSamples,
        isCollapsed,
        isInitiallyCollapsed,
      };
    }
  );
  const mapStateToProps = (state: BenchlingState, props: BaseInputTableProps): ConnectedProps =>
    mapSubStateToProps(
      state.models.workflows,
      state.ui.collapsedTables[createInputTableCollapseId(props.stageEntryId)],
      props.stageEntryId,
      props.viewer
    );
  return mapStateToProps;
}

function getIsCollapsed(
  reduxCollapsedState: boolean | undefined,
  viewer: boolean | undefined,
  orderedInputSamples: Array<{stageRunId: string; stageRunSampleId: string}>,
  parentIdsByStageRunId: {[stageRunId: string]: Array<string>},
  stageRunsById: {[stageRunId: string]: StageRun},
  stageRuns: Array<StageRun>
): {isCollapsed: boolean; isInitiallyCollapsed: boolean} {
  const {rowCount, cellCount} = getDimensions({
    orderedInputSamples,
    parentIdsByStageRunId,
    stageRunsById,
    stageRuns,
  });

  const isInitiallyCollapsed = shouldCollapseTableInitially(cellCount, rowCount, null);
  let isCollapsed: boolean;
  isCollapsed = reduxCollapsedState ?? isInitiallyCollapsed;
  // Never collapse the table in print mode.
  if (viewer) {
    isCollapsed = false;
  }
  return {
    isCollapsed,
    isInitiallyCollapsed,
  };
}

interface BaseInputTableProps {
  stageEntryId: string;
  locked: boolean;
  viewer?: boolean;
}

interface ConnectedProps {
  stageRuns: Array<StageRun>;
  stageRunSampleById: {[stageRunSampleId: string]: StageRunSample};
  stageRunsById: {[stageRunId: string]: StageRun} | null;
  parentIdsByStageRunId: {[stageRunId: string]: Array<string>} | null;
  inputTableConfig: InputTableConfig | null;
  orderedInputSamples: Array<{stageRunId: string; stageRunSampleId: string}>;
  isCollapsed: boolean;
  isInitiallyCollapsed: boolean;
}

interface BaseState {
  headerCheckBoxSelected: boolean;
  selectedRowIds: {[rowId: string]: boolean};
  sortColumnId: string | null;
  sortDirection: SortDirection | null;
}

interface InputSampleModalOpenState extends BaseState {
  isAddInputSampleModalOpen: true;
  addInputSampleModalInitialExternalState: {
    stageRunId: string;
    inputType: InputType | null;
  };
}

interface InputSampleModalClosedState extends BaseState {
  isAddInputSampleModalOpen: false;
  addInputSampleModalInitialExternalState: null;
}

type State = InputSampleModalOpenState | InputSampleModalClosedState;

type InputTableProps = BaseInputTableProps & ConnectedProps;

class InputTable extends React.Component<InputTableProps, State> {
  override state: State = {
    isAddInputSampleModalOpen: false,
    addInputSampleModalInitialExternalState: null,
    headerCheckBoxSelected: false,
    selectedRowIds: {},
    ...getSortStateFromInputTableConfig(this.props.stageRuns, this.props.inputTableConfig),
  };

  _isMounted = false;

  override shouldComponentUpdate(nextProps: InputTableProps, nextState: State): boolean {
    // Shallow comparison doesn't work because mapStateToProps creates new objects on every update
    return !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);
  }

  override componentDidMount(): void {
    this._isMounted = true;
    CollapsedTableReduxActions.init(
      createInputTableCollapseId(this.props.stageEntryId),
      this.props.isCollapsed
    );
  }

  override componentWillUnmount(): void {
    this._isMounted = false;
  }

  _toggleTableCollapsed = (): void => {
    CollapsedTableReduxActions.toggle(createInputTableCollapseId(this.props.stageEntryId));
  };

  _handleOpenAddInputSampleModal = (stageRunId: string, inputType: InputType | null): void => {
    this.setState({
      isAddInputSampleModalOpen: true,
      addInputSampleModalInitialExternalState: {
        stageRunId,
        inputType,
      },
    });
  };

  _handleCloseAddInputSampleModal = (): void => {
    this.setState({
      isAddInputSampleModalOpen: false,
      addInputSampleModalInitialExternalState: null,
    });
  };

  _toggleSelection(rowId: string): void {
    if (this.state.selectedRowIds[rowId]) {
      this.setState({selectedRowIds: {...this.state.selectedRowIds, [rowId]: false}});
    } else {
      this.setState({selectedRowIds: {...this.state.selectedRowIds, [rowId]: true}});
    }
  }

  _renderCheckbox(rowId: string): JSX.Element {
    return (
      <RichObjectTable.CheckboxCell
        checked={Boolean(this.state.selectedRowIds[rowId])}
        onChange={() => {
          this._toggleSelection(rowId);
        }}
        disabled={this.props.locked}
      />
    );
  }

  _toggleHeaderCheckbox = (): void => {
    if (this.state.headerCheckBoxSelected) {
      this.setState({
        headerCheckBoxSelected: false,
        selectedRowIds: {},
      });
    } else {
      const selectedRowIds: {[rowId: string]: boolean} = {};
      const rows = getInputRowDatas(
        this.props.orderedInputSamples,
        this.props.parentIdsByStageRunId || {},
        this.props.stageRunsById || {},
        this.props.stageRuns
      );
      for (const rowData of rows) {
        selectedRowIds[this._getRowId(rowData)] = true;
      }
      this.setState({
        headerCheckBoxSelected: true,
        selectedRowIds,
      });
    }
  };

  _renderHeaderCheckbox(): JSX.Element | null {
    if (this.props.locked) {
      return null;
    }
    return (
      <RichObjectTable.CheckboxCell
        checked={this.state.headerCheckBoxSelected}
        onChange={this._toggleHeaderCheckbox}
      />
    );
  }

  _getRowId(rowData: InputTableRow): string {
    if (rowData.sampleId != null) {
      return `${rowData.stageRun.id}:${rowData.sampleId}`;
    } else if (rowData.unfulfilledParents && rowData.unfulfilledParents.length > 0) {
      return `${rowData.stageRun.id}:pending`;
    } else {
      return `${rowData.stageRun.id}:empty`;
    }
  }

  _handleClickAutoselectContainersButton = async (e: ElementMouseEvent): Promise<void> => {
    e.stopPropagation();
    await StageRunActions.autoselectInputSampleContainers(
      this.props.stageEntryId,
      getSelectedSamples(this.props.orderedInputSamples, this.state.selectedRowIds)
    );
    if (this._isMounted) {
      this.setState({selectedRowIds: {}});
    }
  };

  _handleClickRemoveButton = (e: ElementMouseEvent): void => {
    e.stopPropagation();
    StageRunActions.bulkRemoveInputSamples(
      this.props.stageEntryId,
      getSelectedSamples(this.props.orderedInputSamples, this.state.selectedRowIds),
      {
        onSuccess: () => {
          this.setState({selectedRowIds: {}});
        },
      }
    );
  };

  _renderDownloadAsCSVButton(): JSX.Element {
    return (
      <InputTableRetriever stageEntryId={this.props.stageEntryId}>
        {(table: Table | null) => (
          <SimpleDownloadAsCSVButton
            buttonStyle={ButtonStyle.TERTIARY_BLUE}
            size={ButtonSize.SMALL}
            disabled={table == null}
            onDownloadAsCSV={() => {
              assert(table != null, 'Should block the UI if input table is not yet loaded.');
              const columnLabels = table.columnLabels as Array<string>;
              const cells = table.table;
              const exportedRows = getPlainTextRawTable({table: cells, headerRow: columnLabels});
              initiateTextDownload(ExportUtil.formatRows(exportedRows, ','), `${table.name}.csv`);
              deprecatedTrackEvent(LEGACY_WORKFLOWS_ANALYTICS_ACTIONS.DOWNLOADED_INPUT_TABLE_AS_CSV);
            }}
          />
        )}
      </InputTableRetriever>
    );
  }

  _renderCreateRequestButton(): JSX.Element {
    const sampleInfos: Array<SampleLookupInfoForCreateRequest> = compact(
      getSelectedSampleIds(
        this.props.orderedInputSamples,
        this.props.stageRunSampleById,
        this.state.selectedRowIds
      )
    ).map((sampleId) => ({
      idForLookup: sampleId,
    }));
    return (
      <CreateRequestFromSamplesButton
        buttonStyle={ButtonStyle.TERTIARY_BLUE}
        className="u-mini-spacing-right"
        sampleInfos={sampleInfos}
        size={ButtonSize.SMALL}
      />
    );
  }

  _renderRightHeader(): JSX.Element | null {
    if (this.props.viewer) {
      return null;
    }

    const selectedSamples = getSelectedSamples(this.props.orderedInputSamples, this.state.selectedRowIds);
    // eslint-disable-next-line no-restricted-syntax
    const containerIds = _.uniq(
      selectedSamples
        .map((sample) => this.props.stageRunSampleById[sample.stageRunSampleId])
        .filter((sample) => sample && sample.containerId)
        .map((sample) => sample.containerId)
    ) as Array<string>;
    return (
      <div className="u-flex-display">
        <div>
          <Tooltip
            title={
              selectedSamples.length
                ? 'Auto-select next available container'
                : 'Select rows to auto-select containers'
            }
          >
            <span>
              <Button
                buttonStyle={ButtonStyle.TERTIARY_BLUE}
                className="u-mini-spacing-right"
                disabled={!selectedSamples.length}
                icon="bulk-select-next-container"
                onClick={this._handleClickAutoselectContainersButton}
                size={ButtonSize.SMALL}
              />
            </span>
          </Tooltip>
        </div>
        {BenchlingConfig.ENABLE_REQUESTS && this._renderCreateRequestButton()}
        <TableContainerActionButtons containerIds={containerIds} shouldShowArchiveButton={true} />
        {this._renderDownloadAsCSVButton()}
        <div>
          <Tooltip title={selectedSamples.length ? 'Remove' : 'Select rows to remove'}>
            <span>
              <Button
                buttonStyle={ButtonStyle.TERTIARY_BLUE}
                className="u-mini-spacing-left"
                disabled={!selectedSamples.length}
                icon="delete"
                onClick={this._handleClickRemoveButton}
                size={ButtonSize.SMALL}
              />
            </span>
          </Tooltip>
        </div>
      </div>
    );
  }

  _renderRow(rowData: InputTableRow): JSX.Element {
    const {stageRun, sampleId, parent, unfulfilledParents} = rowData;
    // eslint-disable-next-line no-restricted-syntax
    const experimentalConditionValueNames = _.pluck(
      getUniqueExperimentalConditionSummaries(this.props.stageRuns),
      'name'
    );
    const rowId = this._getRowId(rowData);

    if (sampleId != null) {
      return parent != null ? (
        <ParentSampleInputTableRow
          key={rowId}
          checkboxCell={this._renderCheckbox(rowId)}
          experimentalConditionValueNames={experimentalConditionValueNames}
          stageRun={stageRun}
          inputSampleId={sampleId}
          stageRunSampleById={this.props.stageRunSampleById}
          locked={this.props.locked}
          viewer={this.props.viewer}
        />
      ) : (
        <ExternalSampleInputTableRow
          key={rowId}
          checkboxCell={this._renderCheckbox(rowId)}
          experimentalConditionValueNames={experimentalConditionValueNames}
          stageRun={stageRun}
          inputSampleId={sampleId}
          stageRunSampleById={this.props.stageRunSampleById}
          locked={this.props.locked}
          viewer={this.props.viewer}
        />
      );
    }

    if (unfulfilledParents && unfulfilledParents.length > 0) {
      return (
        <PendingInputTableRow
          key={rowId}
          checkboxCell={this._renderCheckbox(rowId)}
          experimentalConditionValueNames={experimentalConditionValueNames}
          stageRun={stageRun}
          pendingParentStageRuns={unfulfilledParents}
          stageRunSampleById={this.props.stageRunSampleById}
          locked={this.props.locked}
        />
      );
    }

    return (
      <EmptyInputTableRow
        key={rowId}
        checkboxCell={this._renderCheckbox(rowId)}
        experimentalConditionValueNames={experimentalConditionValueNames}
        stageRun={stageRun}
        locked={this.props.locked}
        onOpenAddInputSampleModal={this._handleOpenAddInputSampleModal}
      />
    );
  }

  _getSortDirection(columnId: string | null): SortDirection | null {
    return this.state.sortColumnId === columnId ? this.state.sortDirection : null;
  }

  _getSortedRows(): Array<InputTableRow> {
    const rowDatas = getInputRowDatas(
      this.props.orderedInputSamples,
      this.props.parentIdsByStageRunId || {},
      this.props.stageRunsById || {},
      this.props.stageRuns
    );
    const {sortColumnId, sortDirection} = this.state;
    return sortInputRowDatas(rowDatas, this.props.stageRunSampleById, {sortColumnId, sortDirection});
  }

  _getInputTableConfigParameters(columnId: string | null, sortOrder: SortDirection | null): InputTableConfig {
    let sortByType = INPUT_TABLE_SORT_BY_TYPE.DEFAULT;
    let sortByExperimentalConditionId = null;
    let isSortReversed = null;

    if (columnId != null && sortOrder != null) {
      switch (columnId) {
        case SAMPLE_COLUMN: {
          sortByType = INPUT_TABLE_SORT_BY_TYPE.SAMPLE;
          break;
        }
        case CONTAINER_COLUMN: {
          sortByType = INPUT_TABLE_SORT_BY_TYPE.CONTAINER;
          break;
        }
        case STAGE_RUN_COLUMN: {
          sortByType = INPUT_TABLE_SORT_BY_TYPE.STAGE_RUN;
          break;
        }
        default: {
          const ec = _.findWhere(getUniqueExperimentalConditionSummaries(this.props.stageRuns), {
            id: columnId,
          });
          if (ec != null) {
            sortByType = INPUT_TABLE_SORT_BY_TYPE.EXPERIMENTAL_CONDITION_VALUE;
            sortByExperimentalConditionId = ec.id;
          }
          break;
        }
      }

      if (sortByType !== INPUT_TABLE_SORT_BY_TYPE.DEFAULT) {
        isSortReversed = sortOrder === DESCENDING_SORT;
      }
    }

    return {sortByType, sortByExperimentalConditionId, isSortReversed};
  }

  _sortColumn(columnId: string | null): void {
    let nextSortOrder;
    if (this.state.sortColumnId === columnId) {
      nextSortOrder = SORT_ORDERS[(SORT_ORDERS.indexOf(this.state.sortDirection) + 1) % SORT_ORDERS.length];
    } else {
      nextSortOrder = ASCENDING_SORT;
    }
    this.setState({sortColumnId: columnId, sortDirection: nextSortOrder});

    StageEntryActions.updateInputTableConfig(
      this.props.stageEntryId,
      this._getInputTableConfigParameters(columnId, nextSortOrder)
    );
  }

  _renderColumnHeader({
    columnId,
    text,
    isSortable,
  }: {
    columnId: string | null;
    text: string;
    isSortable: boolean;
  }): JSX.Element {
    return (
      <ScrollableTableHeaderCell
        column={{
          isSortable,
          sortKey: columnId,
          accessorKey: columnId ?? text,
          sortDirection: this._getSortDirection(columnId),
          header: text,
        }}
        handleSort={() => {
          this._sortColumn(columnId);
        }}
        isPrimary={true}
        isCollapsed={false}
      />
    );
  }

  _getHeaderCells(): Array<JSX.Element | null> {
    return [
      this._renderHeaderCheckbox(),
      this._renderColumnHeader({columnId: SAMPLE_COLUMN, text: SAMPLE_COLUMN_LABEL, isSortable: true}),
      // Sorting by container is disabled because containers are loaded asynchroneously and we
      // don't want the table to get updated automatically once it is displayed.
      this._renderColumnHeader({columnId: CONTAINER_COLUMN, text: CONTAINER_COLUMN_LABEL, isSortable: false}),
      this._renderColumnHeader({columnId: STAGE_RUN_COLUMN, text: STAGE_RUN_COLUMN_LABEL, isSortable: true}),
      ...getUniqueExperimentalConditionSummaries(this.props.stageRuns).map((ec) =>
        this._renderColumnHeader({columnId: ec.id, text: ec.name, isSortable: true})
      ),
    ];
  }

  _handleInsertFromSpreadsheet = (): void => {
    BulkAddSamplesToInputTableFlow.open({
      stageEntryId: this.props.stageEntryId,
    });
  };

  _renderLeftHeader(): JSX.Element {
    const isAddDisabled = this.props.stageRuns.length === 0;
    return (
      <div className="u-vertical-center-items">
        <div>Input Samples</div>
        {!this.props.locked && (
          <Dropdown
            className="workflowTable-addDropdown"
            menu={
              <Menu width={Menu.WIDTHS.LARGE} height={Menu.HEIGHTS.MEDIUM}>
                <Option
                  icon="compose"
                  onSelect={() => {
                    this._handleOpenAddInputSampleModal(this.props.stageRuns[0].id, null);
                  }}
                >
                  Add input sample
                </Option>
                <Option icon="upload" onSelect={this._handleInsertFromSpreadsheet}>
                  Import from spreadsheet
                </Option>
              </Menu>
            }
          >
            <RichObjectTable.AddButton className="u-mini-spacing-left" disabled={isAddDisabled} />
          </Dropdown>
        )}
      </div>
    );
  }

  override render(): JSX.Element {
    if (!this.props.parentIdsByStageRunId || !this.props.stageRunsById) {
      assert(false, 'InputTable should never be rendered in a loading state.');
    }

    if (!StageEntryStore.getEditorState(this.props.stageEntryId)!.isLatest) {
      return <UnversionedTable title="Input Samples" />;
    }

    const rows = this._getSortedRows();
    return (
      <div {...getTestProps({component: 'InputTable', element: 'root'})}>
        <ModalAnchor open={this.state.isAddInputSampleModalOpen}>
          {this.state.isAddInputSampleModalOpen && (
            <AddInputSampleModal
              stageRuns={this.props.stageRuns}
              stageRunSampleById={this.props.stageRunSampleById}
              onRequestClose={this._handleCloseAddInputSampleModal}
              initialExternalState={this.state.addInputSampleModalInitialExternalState}
            />
          )}
        </ModalAnchor>
        {/* eslint-disable-next-line react/jsx-no-constructed-context-values -- Disabling for now, but worth fixing */}
        <TableCollapserContext.Provider value={{isTableInitiallyCollapsed: this.props.isInitiallyCollapsed}}>
          <RichObjectTable.Table
            className="noteEditor-richObjectTable"
            collapsedHeaderText={`${rows.length} ${pluralize(rows.length, 'row')}`}
            columns={[
              <RichObjectTable.CheckboxCol key="firstCol" />,
              ..._.range(this._getHeaderCells().length - 1).map((i) => (
                <col key={i} width={RichObjectTable.CONTENT_COLUMN_MIN_WIDTH} />
              )),
            ]}
            headerCells={this._getHeaderCells()}
            isCollapsed={this.props.isCollapsed}
            isCollapsible={true}
            leftHeader={this._renderLeftHeader()}
            rightHeader={this._renderRightHeader()}
            onToggleCollapsed={this._toggleTableCollapsed}
          >
            {rows.map((rowData) => this._renderRow(rowData))}
          </RichObjectTable.Table>
        </TableCollapserContext.Provider>
      </div>
    );
  }
}

const ConnectedInputTable: React.ComponentType<BaseInputTableProps> =
  connect(makeMapStateToProps)(InputTable);

export default ConnectedInputTable;
